/**
 * Workspace Team API
 *
 * API functions for workspace-level team management.
 * 
 * ACCESS MODEL:
 * - Workspace membership grants access to ALL properties attached to that workspace
 * - No per-user property allowlists
 * - Seats are enforced account-wide
 * 
 * BACKEND STATUS:
 * - Uses fallback to account-level APIs where workspace-specific endpoints don't exist yet
 * - TODO: Remove fallbacks when backend workspace endpoints are implemented
 */

import type {
  WorkspaceMember,
  WorkspaceInvite,
  GetWorkspaceMembersResponse,
  GetWorkspaceInvitesResponse,
  WorkspaceTeamStats,
  WorkspaceProperty,
  GetWorkspacePropertiesResponse,
  AvailableProperty,
  WorkspaceRole,
  WorkspaceConnector,
  GetWorkspaceConnectorsResponse,
  CreateWorkspaceConnectorPayload,
  UpdateWorkspaceConnectorPayload,
  WorkspaceRevenueConfig,
} from "@/lib/types/workspace-team"

const PUBLIC_IS_PROD = import.meta.env.VITE_PUBLIC_IS_PROD;
const PUBLIC_IS_LOCAL = import.meta.env.VITE_PUBLIC_IS_LOCAL;

const API_URL =
  PUBLIC_IS_PROD === "TRUE"
    ? `https://app.humblytics.com/api/v1/`
    : PUBLIC_IS_LOCAL === "TRUE"
    ? `http://localhost:3000/api/v1/`
    : `https://humblytics.onrender.com/api/v1/`;

/**
 * Helper for GET requests
 */
async function getRequest<T>(endpoint: string, token: string): Promise<T> {
  const response = await fetch(API_URL + endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  return response.json()
}

/**
 * Helper for POST requests
 */
async function postRequest<T>(
  endpoint: string,
  body: Record<string, unknown>,
  token: string
): Promise<T> {
  const response = await fetch(API_URL + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  return response.json()
}

/**
 * Helper for PATCH requests
 */
async function patchRequest<T>(
  endpoint: string,
  body: Record<string, unknown>,
  token: string
): Promise<T> {
  const response = await fetch(API_URL + endpoint, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  return response.json()
}

/**
 * Helper for DELETE requests
 */
async function deleteRequest<T>(endpoint: string, token: string): Promise<T> {
  const response = await fetch(API_URL + endpoint, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  return response.json()
}

/**
 * Get all members of a workspace
 * 
 * NEW MODEL: Workspace endpoints are the source of truth.
 * No fallback to account-level APIs.
 */
export async function getWorkspaceMembers(
  workspaceId: string,
  token: string
): Promise<GetWorkspaceMembersResponse> {
  return await getRequest<GetWorkspaceMembersResponse>(
    `workspaces/${workspaceId}/members`,
    token
  )
}

/**
 * Get pending invites for a workspace
 * 
 * NEW MODEL: Workspace endpoints are the source of truth.
 * No fallback to account-level APIs.
 */
export async function getWorkspaceInvites(
  workspaceId: string,
  token: string
): Promise<GetWorkspaceInvitesResponse> {
  return await getRequest<GetWorkspaceInvitesResponse>(
    `workspaces/${workspaceId}/invites`,
    token
  )
}

/**
 * Invite a user to a workspace
 * 
 * NEW MODEL: 
 * - Workspace membership grants access to ALL properties in that workspace
 * - No per-property access control within a workspace
 */
export async function inviteToWorkspace(
  workspaceId: string,
  email: string,
  role: WorkspaceRole = 'member',
  token: string
): Promise<{ success: boolean; invite?: WorkspaceInvite }> {
  return await postRequest<{ success: boolean; invite?: WorkspaceInvite }>(
    `workspaces/${workspaceId}/invites`,
    { email, role },
    token
  )
}

/**
 * Remove a member from a workspace
 * 
 * NEW MODEL: Removing from workspace removes access to all properties in that workspace.
 */
export async function removeFromWorkspace(
  workspaceId: string,
  userId: string,
  token: string
): Promise<{ success: boolean }> {
  return await deleteRequest<{ success: boolean }>(
    `workspaces/${workspaceId}/members/${userId}`,
    token
  )
}

/**
 * Resend an invitation for a workspace
 */
export async function resendWorkspaceInvite(
  workspaceId: string,
  inviteId: string,
  token: string
): Promise<{ success: boolean }> {
  return await postRequest<{ success: boolean }>(
    `workspaces/${workspaceId}/invites/${inviteId}/resend`,
    {},
    token
  )
}

/**
 * Cancel an invitation for a workspace
 */
export async function cancelWorkspaceInvite(
  workspaceId: string,
  inviteId: string,
  token: string
): Promise<{ success: boolean }> {
  return await deleteRequest<{ success: boolean }>(
    `workspaces/${workspaceId}/invites/${inviteId}`,
    token
  )
}

/**
 * Update a member's role within a workspace
 */
export async function updateMemberRole(
  workspaceId: string,
  userId: string,
  role: WorkspaceRole,
  token: string
): Promise<{ success: boolean }> {
  return await postRequest<{ success: boolean }>(
    `workspaces/${workspaceId}/members/${userId}`,
    { role },
    token
  )
}

// ============================================================================
// Workspace-Property APIs (1:1 Model - Each property belongs to exactly ONE workspace)
// ============================================================================

/**
 * Get all properties belonging to a workspace
 */
export async function getWorkspaceProperties(
  workspaceId: string,
  token: string
): Promise<GetWorkspacePropertiesResponse> {
  try {
    return await getRequest<GetWorkspacePropertiesResponse>(
      `workspaces/${workspaceId}/properties`,
      token
    )
  } catch {
    // Fallback: return empty (properties will come from workspace context)
    return { properties: [], total: 0 }
  }
}

/**
 * Move a property to a workspace (enforces 1:1 - removes from previous workspace)
 * This replaces the old "attach" semantics with "move" semantics.
 */
export async function movePropertyToWorkspace(
  workspaceId: string,
  propertyId: string,
  token: string
): Promise<{ success: boolean; property?: WorkspaceProperty }> {
  return await postRequest<{ success: boolean; property?: WorkspaceProperty }>(
    `workspaces/${workspaceId}/properties/${propertyId}`,
    {},
    token
  )
}

/**
 * Update a property's workspace-level settings (e.g., enabled status)
 */
export async function updateWorkspaceProperty(
  workspaceId: string,
  propertyId: string,
  updates: { enabled?: boolean },
  token: string
): Promise<{ success: boolean; property?: WorkspaceProperty }> {
  return await patchRequest<{ success: boolean; property?: WorkspaceProperty }>(
    `workspaces/${workspaceId}/properties/${propertyId}`,
    updates,
    token
  )
}

/**
 * Remove a property from a workspace (unassigns it - property becomes orphaned)
 * In practice, you may want to move it to a default workspace instead.
 */
async function removePropertyFromWorkspace(
  workspaceId: string,
  propertyId: string,
  token: string
): Promise<{ success: boolean }> {
  return await deleteRequest<{ success: boolean }>(
    `workspaces/${workspaceId}/properties/${propertyId}`,
    token
  )
}

/**
 * Get all properties available to the account
 * Returns properties with their current workspace assignment (1:1)
 */
export async function getAvailableProperties(
  token: string
): Promise<{ properties: AvailableProperty[] }> {
  try {
    return await getRequest<{ properties: AvailableProperty[] }>(
      "properties",
      token
    )
  } catch {
    // Fallback: return empty
    return { properties: [] }
  }
}

/**
 * Get team statistics for a workspace
 * Calculated from members and subscription data
 */
async function getWorkspaceTeamStats(
  workspaceId: string,
  token: string,
  subscription?: { teamSeatLimit?: number }
): Promise<WorkspaceTeamStats> {
  const [membersResponse, invitesResponse] = await Promise.all([
    getWorkspaceMembers(workspaceId, token),
    getWorkspaceInvites(workspaceId, token),
  ])

  const seatLimit = subscription?.teamSeatLimit || 1
  const isUnlimited = seatLimit === 999999999
  const usedSeats = membersResponse.total + invitesResponse.total
  const availableSeats = isUnlimited
    ? Infinity
    : Math.max(0, seatLimit - usedSeats)

  return {
    total_members: membersResponse.total,
    pending_invites: invitesResponse.total,
    seat_limit: seatLimit,
    is_unlimited: isUnlimited,
    used_seats: usedSeats,
    available_seats: isUnlimited ? Infinity : availableSeats,
  }
}

// ============================================================================
// Workspace Connector APIs (Workspace-scoped)
// ============================================================================

/**
 * Get all connectors for a workspace
 */
async function getWorkspaceConnectors(
  workspaceId: string,
  token: string
): Promise<GetWorkspaceConnectorsResponse> {
  try {
    return await getRequest<GetWorkspaceConnectorsResponse>(
      `workspaces/${workspaceId}/connectors`,
      token
    )
  } catch {
    return { connectors: [], total: 0 }
  }
}

/**
 * Create a new connector in a workspace
 */
async function createWorkspaceConnector(
  workspaceId: string,
  payload: CreateWorkspaceConnectorPayload,
  token: string
): Promise<{ success: boolean; connector?: WorkspaceConnector }> {
  return await postRequest<{ success: boolean; connector?: WorkspaceConnector }>(
    `workspaces/${workspaceId}/connectors`,
    payload as unknown as Record<string, unknown>,
    token
  )
}/**
 * Update a workspace connector
 */
async function updateWorkspaceConnector(
  workspaceId: string,
  connectorId: string,
  updates: UpdateWorkspaceConnectorPayload,
  token: string
): Promise<{ success: boolean; connector?: WorkspaceConnector }> {
  return await patchRequest<{ success: boolean; connector?: WorkspaceConnector }>(
    `workspaces/${workspaceId}/connectors/${connectorId}`,
    updates as unknown as Record<string, unknown>,
    token
  )
}

/**
 * Delete a workspace connector
 */
async function deleteWorkspaceConnector(
  workspaceId: string,
  connectorId: string,
  token: string
): Promise<{ success: boolean }> {
  return await deleteRequest<{ success: boolean }>(
    `workspaces/${workspaceId}/connectors/${connectorId}`,
    token
  )
}

// ============================================================================
// Workspace Revenue Config APIs (Workspace-scoped)
// ============================================================================

/**
 * Get revenue configuration for a workspace
 */
export async function getWorkspaceRevenueConfig(
  workspaceId: string,
  token: string
): Promise<WorkspaceRevenueConfig> {
  try {
    return await getRequest<WorkspaceRevenueConfig>(
      `workspaces/${workspaceId}/revenue-config`,
      token
    )
  } catch {
    return {}
  }
}

/**
 * Update revenue configuration for a workspace
 */
async function updateWorkspaceRevenueConfig(
  workspaceId: string,
  config: Partial<WorkspaceRevenueConfig>,
  token: string
): Promise<{ success: boolean; config?: WorkspaceRevenueConfig }> {
  return await patchRequest<{ success: boolean; config?: WorkspaceRevenueConfig }>(
    `workspaces/${workspaceId}/revenue-config`,
    config,
    token
  )
}
